/* Cart UX: quantity that saves itself, and controls in the drawer.
 *
 * Two problems this fixes.
 *
 * 1. The cart page's quantity field is a plain <input name="updates[]"> inside a native form, so it only
 *    submitted on Enter. Anyone who typed a number and tapped elsewhere lost the edit. change fires when the
 *    field loses focus AND the value differs, so that is the hook - and requestSubmit() reuses the form's own
 *    submit path rather than inventing a second one, so whatever the theme does with the form still happens.
 *
 * 2. The drawer showed "Qty 2" as static text with no way to change or remove anything - you had to open the
 *    full cart. Each line now carries a - / + stepper and a remove button.
 *
 * Kept out of mstore-modern-core.js on purpose: that file renders the drawer, and a syntax error there would
 * take the whole cart with it. This is additive, guarded, and does nothing if its hooks are absent.
 */
(() => {
  if (window.__msCart) return;
  window.__msCart = true;

  const money = (cents) => {
    try {
      return (cents / 100).toFixed(2);
    } catch (err) {
      return '';
    }
  };

  /* The drawer is server-rendered, so the honest refresh is to fetch the section again and swap it in. The
     section URL is already on the element - the theme put it there for its own use. */
  const refreshDrawer = async () => {
    const drawer = document.querySelector('[data-ms-cart-drawer]');
    if (!drawer) return;
    const url = drawer.getAttribute('data-section-url');
    if (!url) return;
    try {
      const res = await fetch(url + (url.indexOf('?') > -1 ? '&' : '?') + 'ms=' + Date.now(), {
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });
      if (!res.ok) return;
      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = doc.querySelector('[data-ms-cart-drawer]') || doc.querySelector('.ms-cart-drawer');
      if (!fresh) return;
      const wasOpen = drawer.classList.contains('is-open') || drawer.getAttribute('aria-hidden') === 'false';
      drawer.replaceWith(fresh);
      syncCounts(doc);
      if (wasOpen) fresh.classList.add('is-open');
    } catch (err) {
      /* the next cart interaction will still be correct; a failed refresh is not worth an error state */
    }
  };

  /* The header badge and any other count in the DOM come from the same render, so take them from the
     freshly fetched document rather than guessing. */
  const syncCounts = (doc) => {
    const freshCount = doc.querySelector('[data-ms-cart-count]');
    if (!freshCount) return;
    const live = document.querySelectorAll('[data-ms-cart-count]');
    live.forEach((el) => {
      el.textContent = freshCount.textContent.trim();
      if (freshCount.hasAttribute('hidden')) el.setAttribute('hidden', '');
      else el.removeAttribute('hidden');
    });
  };

  /* Section Rendering: /cart/change.js and /cart/add.js return the re-rendered drawer (and the cart page
     section, on the cart page) in the same response as the cart, so a change costs one round trip rather
     than a change followed by a second fetch of the drawer. */
  const drawerSectionId = () => {
    const drawer = document.querySelector('[data-ms-cart-drawer]');
    const url = drawer ? drawer.getAttribute('data-section-url') || '' : '';
    const match = url.match(/[?&]section_id=([^&]+)/);
    return match ? decodeURIComponent(match[1]) : '';
  };
  const pageSectionId = () => {
    const page = document.querySelector('.ms-cart-page');
    const wrapper = page ? page.closest('.shopify-section') : null;
    return wrapper && wrapper.id ? wrapper.id.replace(/^shopify-section-/, '') : '';
  };
  const sectionsToRender = () => [drawerSectionId(), pageSectionId()].filter(Boolean);

  const setCounts = (count) => {
    document.querySelectorAll('[data-ms-cart-count]').forEach((el) => {
      el.textContent = String(count);
      if (count > 0) el.removeAttribute('hidden');
      else el.setAttribute('hidden', '');
    });
  };

  const applySections = (sections) => {
    if (!sections) return false;
    let applied = false;
    const drawerId = drawerSectionId();
    if (drawerId && sections[drawerId]) {
      const drawer = document.querySelector('[data-ms-cart-drawer]');
      const doc = new DOMParser().parseFromString(sections[drawerId], 'text/html');
      const fresh = doc.querySelector('[data-ms-cart-drawer]');
      if (drawer && fresh) {
        const wasOpen = drawer.classList.contains('is-open');
        drawer.replaceWith(fresh);
        if (wasOpen) fresh.classList.add('is-open');
        applied = true;
      }
    }
    const pageId = pageSectionId();
    if (pageId && sections[pageId]) {
      const wrapper = document.getElementById('shopify-section-' + pageId);
      const doc = new DOMParser().parseFromString(sections[pageId], 'text/html');
      const fresh = doc.getElementById('shopify-section-' + pageId);
      if (wrapper && fresh) {
        wrapper.replaceWith(fresh);
        applied = true;
      }
    }
    return applied;
  };

  /* Apply a cart change and re-render from the same response; falls back to a separate drawer fetch, and on
     the cart page to a reload, if the sections do not come back. */
  const cartRequest = async (endpoint, payload) => {
    const res = await fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(Object.assign({}, payload, { sections: sectionsToRender(), sections_url: window.location.pathname }))
    });
    if (!res.ok) throw new Error('Cart request failed: ' + res.status);
    return res.json();
  };

  const afterChange = async (data, onCartPage) => {
    if (data && typeof data.item_count === 'number') setCounts(data.item_count);
    if (applySections(data && data.sections)) return;
    if (onCartPage) window.location.reload();
    else await refreshDrawer();
  };

  const changeLine = async (line, quantity, onCartPage) => {
    try {
      const data = await cartRequest('/cart/change.js', { line: Number(line), quantity: Number(quantity) });
      await afterChange(data, onCartPage);
    } catch (err) {
      /* the server state is authoritative: show it */
      if (onCartPage) window.location.reload();
      else await refreshDrawer();
    }
  };

  /* 1. Quantity on the cart page: save on blur when the value changed. */
  document.addEventListener('change', (event) => {
    const input = event.target.closest('input[name="updates[]"]');
    if (!input || !input.form) return;
    if (input.form.requestSubmit) input.form.requestSubmit();
    else input.form.submit();
  });

  /* 4. Cart page Remove: the link goes to /cart/change and back, two full page loads. Handled in place
     instead - the row fades at once and the page section re-renders from the change response. The link
     still works without JavaScript. */
  document.addEventListener('click', (event) => {
    const link = event.target.closest('.ms-cart-row a[href*="/cart/change"]');
    if (!link) return;
    let url;
    try { url = new URL(link.href, window.location.href); } catch (err) { return; }
    const line = url.searchParams.get('line');
    const quantity = url.searchParams.get('quantity');
    if (!line || quantity === null) return;
    event.preventDefault();
    const row = link.closest('.ms-cart-row');
    if (row) row.classList.add('is-removing');
    changeLine(line, quantity, true);
  });

  /* 3. Jibbitz cross-sell (snippets/mstore-cart-jibbitz.liquid), in the drawer and on the cart page. One tap
     adds the charm; the drawer then re-renders in place, so the new line - and the Jibbitz discount on it -
     appear without closing it; on the cart page the page section re-renders the same way. */
  document.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-ms-cart-addon-add]');
    if (!button || button.disabled) return;
    event.preventDefault();
    const id = Number(button.getAttribute('data-variant-id'));
    if (!id) return;
    button.disabled = true;
    button.textContent = 'Adding…';
    let added = null;
    const addLine = async () => {
      added = await cartRequest('/cart/add.js', { items: [{ id, quantity: 1 }] });
      const cart = await fetch('/cart.js').then((r) => r.json());
      // the add response describes the added items, not the cart, so carry the cart's count with it
      added.item_count = cart.item_count;
      return { cart };
    };
    const inDrawer = Boolean(button.closest('[data-ms-cart-drawer]'));
    try {
      const standard = window.MStoreStandardEvents;
      // same context names the theme uses for shopify:cart:view - the drawer is the dialog
      if (standard) await standard.cartLinesUpdate(button, { action: 'add', context: inDrawer ? 'dialog' : 'page', lines: [{ merchandiseId: id, quantity: 1 }], run: addLine });
      else await addLine();
      await afterChange(added, !inDrawer);
    } catch (err) {
      button.disabled = false;
      button.textContent = 'Try again';
    }
  });

  /* 2. Drawer controls, delegated so they survive the drawer being replaced. */
  document.addEventListener('click', (event) => {
    const step = event.target.closest('[data-cart-delta]');
    const remove = event.target.closest('[data-cart-remove]');
    const target = step || remove;
    if (!target) return;
    event.preventDefault();
    const line = target.getAttribute('data-cart-line');
    if (!line) return;

    const row = target.closest('.ms-cart-item');

    if (remove) {
      if (row) row.classList.add('is-removing');
      changeLine(line, 0, false);
      return;
    }

    const valueEl = row ? row.querySelector('[data-cart-qty]') : null;
    const current = valueEl ? parseInt(valueEl.textContent.trim(), 10) : NaN;
    if (isNaN(current)) return;
    const next = current + parseInt(target.getAttribute('data-cart-delta'), 10);
    if (next < 0) return;
    if (valueEl) valueEl.textContent = String(next < 1 ? 0 : next);
    if (next === 0 && row) row.classList.add('is-removing');
    changeLine(line, next, false);
  });
  /* Cart count bump. The badge is written from three places (syncCounts and setCounts above, and
     mstore-modern-core.js), so this watches the badge rather than patching each writer. It only reacts when
     the number grows: removing an item is not a moment to celebrate. A class on a CSS transition, not a
     keyframe, so quick repeated adds retarget instead of restarting. */
  const bumpCount = (el) => {
    el.classList.add('is-bumped');
    clearTimeout(el.__msBumpTimer);
    el.__msBumpTimer = setTimeout(() => el.classList.remove('is-bumped'), 120);
  };
  if ('MutationObserver' in window) {
    document.querySelectorAll('[data-ms-cart-count]').forEach((el) => {
      let last = el.textContent.trim();
      new MutationObserver(() => {
        const next = el.textContent.trim();
        if (next === last) return;
        const grew = Number(next) > Number(last);
        last = next;
        if (grew) bumpCount(el);
      }).observe(el, { childList: true, characterData: true, subtree: true });
    });
  }
})();
